import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { FaTrophy, FaCheckCircle, FaClock, FaTimesCircle, FaArrowRight, FaUsers, FaCalendarAlt } from 'react-icons/fa'
import { MdCardGiftcard } from 'react-icons/md'

import { SectionHeader } from '../ui'
import HomeSection from './HomeSection'
import { get_my_giveaways } from '../../store/reducers/giveawayReducer'

// MyGiveawaysHomeStrip — small rail of the customer's joined giveaways.
// Only renders for logged-in customers. Guests → null. Logged-in users with
// zero joined giveaways see a single compact empty-state card (never a large
// blank section); nothing renders until the first fetch settles, so the
// empty state can't flash before data arrives.

// `key` maps to home.myGiveaways.status.* in the locale files; the visual
// classes/icons stay here (presentation, not copy).
const RESULT = {
    active:         { key: 'active',         cls: 'bg-emerald-50 text-emerald-700',  icon: <FaCheckCircle size={9} /> },
    pending_result: { key: 'pending_result', cls: 'bg-amber-50 text-amber-700',      icon: <FaClock size={9} /> },
    winner:         { key: 'winner',         cls: 'bg-yellow-50 text-yellow-800',    icon: <FaTrophy size={9} /> },
    not_winner:     { key: 'not_winner',     cls: 'bg-slate-50 text-slate-600',      icon: <FaTimesCircle size={9} /> },
    cancelled:      { key: 'cancelled',      cls: 'bg-rose-50 text-rose-700',        icon: <FaTimesCircle size={9} /> },
    unknown:        { key: 'unknown',        cls: 'bg-slate-50 text-slate-500',      icon: null },
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('az-AZ') : '—'

const MyGiveawaysHomeStrip = () => {
    const dispatch = useDispatch()
    const { t } = useTranslation()
    const { userInfo } = useSelector(s => s.auth)
    const { myGiveaways } = useSelector(s => s.giveaway)
    const [ready, setReady] = useState(false)

    // Guests never touch the protected endpoint — no 401 noise, no waste.
    useEffect(() => {
        if (!userInfo?.id) return
        dispatch(get_my_giveaways({ page: 1, limit: 3, filter: 'all' }))
            .finally(() => setReady(true))
    }, [dispatch, userInfo?.id])

    if (!userInfo || !ready) return null

    const top = Array.isArray(myGiveaways) ? myGiveaways.slice(0, 3) : []

    return (
        <HomeSection>
            <SectionHeader
                eyebrow={{ icon: <MdCardGiftcard />, label: t('home.myGiveaways.eyebrow'), tone: 'brand' }}
                title={t('home.myGiveaways.title')}
                subtitle={t('home.myGiveaways.subtitle')}
                action={{ to: '/dashboard/giveaways', label: t('buttons.viewAll') }}
            />
            {top.length === 0 ? (
                <div className='flex items-center gap-4 rounded-card border border-slate-200 bg-white px-5 py-4 shadow-card sm:flex-col sm:items-start sm:gap-3'>
                    <span className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand-50 text-brand-600'>
                        <MdCardGiftcard size={20} />
                    </span>
                    <div className='min-w-0 flex-1'>
                        <p className='text-sm font-bold text-slate-900'>{t('home.myGiveaways.emptyTitle')}</p>
                        <p className='text-xs text-slate-500'>{t('home.myGiveaways.emptySubtitle')}</p>
                    </div>
                    <Link
                        to='/cekilisler'
                        className='inline-flex shrink-0 items-center gap-1.5 rounded-card bg-brand-600 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-brand-700'
                    >
                        {t('home.myGiveaways.emptyCta')} <FaArrowRight size={9} />
                    </Link>
                </div>
            ) : (
                <div className='grid grid-cols-3 md-lg:grid-cols-2 sm:grid-cols-1 gap-4'>
                    {top.map(row => {
                        const result = RESULT[row.resultStatus] || RESULT.unknown
                        return (
                            <div
                                key={row.giveawayId}
                                className='flex flex-col overflow-hidden rounded-card border border-slate-200 bg-white shadow-card hover:shadow-card-hover transition-shadow'
                            >
                                {/* Cover — fixed compact height (never grows with column width) */}
                                <div className='relative h-40 sm:h-36 bg-gradient-to-br from-violet-100 to-amber-50'>
                                    {row.coverImage ? (
                                        <img
                                            src={row.coverImage}
                                            alt={row.title}
                                            loading='lazy'
                                            decoding='async'
                                            className='h-full w-full object-cover'
                                        />
                                    ) : (
                                        <div className='flex h-full w-full items-center justify-center text-violet-500'>
                                            <FaTrophy size={26} className='opacity-60' />
                                        </div>
                                    )}
                                    <span className={`absolute left-2 top-2 inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[10px] font-bold ${result.cls}`}>
                                        {result.icon}{t(`home.myGiveaways.status.${result.key}`)}
                                    </span>
                                </div>
                                <div className='flex flex-1 flex-col gap-1.5 p-3'>
                                    <h3 className='line-clamp-2 text-sm font-bold text-slate-900'>{row.title}</h3>
                                    <div className='flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500'>
                                        {Number.isFinite(Number(row.participantCount)) && (
                                            <span className='inline-flex items-center gap-1'>
                                                <FaUsers size={9} className='text-slate-400' /> {row.participantCount} iştirakçı
                                            </span>
                                        )}
                                        {Number.isFinite(Number(row.winnerCount)) && row.winnerCount > 0 && (
                                            <span className='inline-flex items-center gap-1'>
                                                <FaTrophy size={9} className='text-amber-500' /> {row.winnerCount} qalib
                                            </span>
                                        )}
                                        {row.endDate && (
                                            <span className='inline-flex items-center gap-1'>
                                                <FaCalendarAlt size={9} className='text-slate-400' /> {fmtDate(row.endDate)}
                                            </span>
                                        )}
                                    </div>
                                    <p className='text-[11px] text-slate-400'>
                                        {t('home.myGiveaways.joinedPrefix')} <span className='font-semibold text-slate-600'>{fmtDate(row.joinedAt)}</span>
                                    </p>
                                    <Link
                                        to={`/cekilis/${encodeURIComponent(row.slug)}`}
                                        className='mt-auto inline-flex items-center justify-center gap-1.5 rounded-card bg-violet-50 px-3 py-1.5 text-[11px] font-semibold text-violet-700 hover:bg-violet-600 hover:text-white transition-colors'
                                    >
                                        {t('home.myGiveaways.details')} <FaArrowRight size={9} />
                                    </Link>
                                </div>
                            </div>
                        )
                    })}
                </div>
            )}
        </HomeSection>
    )
}

export default MyGiveawaysHomeStrip
